import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { UmssSelectComponent, SelectOption } from '../select/select.component';
import { UmssButtonGroupComponent } from '../button-group/button-group.component';
import { UmssToggleButtonComponent } from '../toggle-button/toggle-button.component';
import {
  JobPositionTypeEnum,
  JOB_POSITION_TYPE_DISPLAY
} from '@umss/shared/data-access';

@Component({
  selector: 'umss-job-position-type-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UmssSelectComponent,
    UmssButtonGroupComponent,
    UmssToggleButtonComponent
  ],
  template: `
    @if (displayMode === 'select') {
      <umss-select
        [options]="options"
        [placeholder]="placeholder"
        [label]="showLabel ? label : undefined"
        [helperText]="helperText"
        [errorMessage]="errorMessage"
        [size]="size"
        [disabled]="disabled"
        [required]="required"
        [clearable]="true"
        [filterStyle]="filterStyle"
        [error]="error"
        [(value)]="value"
        (selectionChange)="onSelectionChange($event)"
        (onBlur)="onBlur()"
      />
    } @else {
      <umss-button-group
        [formControl]="internalControl"
        [label]="showLabel ? label : undefined"
        [size]="size"
        [variant]="variant"
        [required]="required"
        [allowWrap]="true"
        [clearable]="false"
        [maxPerRow]="maxPerRow"
      >
        @for (option of options; track option.value) {
          <umss-toggle-button [label]="option.label" [value]="option.value" />
        }
      </umss-button-group>
    }
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssJobPositionTypeSelectComponent),
      multi: true
    }
  ]
})
export class UmssJobPositionTypeSelectComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() displayMode: 'select' | 'button-group' = 'select';
  @Input() showLabel: boolean = false;
  @Input() label?: string = 'Position type';
  @Input() placeholder: string = 'Select a position type';
  @Input() helperText?: string;
  @Input() errorMessage?: string;
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() filterStyle: 'outlined' | 'elevated' = 'outlined';
  @Input() variant: 'outlined' | 'ghost' = 'outlined';
  @Input() set disabled(value: boolean) {
    this._disabled = value;
    this._syncDisabled();
  }
  get disabled(): boolean {
    return this._disabled;
  }
  @Input() required = false;
  @Input() error = false;
  @Input() maxPerRow?: number;

  @Input() set value(val: JobPositionTypeEnum | null) {
    this._value = val;
  }
  get value(): JobPositionTypeEnum | null {
    return this._value;
  }

  @Output() valueChange = new EventEmitter<JobPositionTypeEnum | null>();

  readonly options: SelectOption[] = Object.values(JobPositionTypeEnum).map(
    (v) => ({ value: v, label: JOB_POSITION_TYPE_DISPLAY[v] })
  );

  readonly internalControl = new FormControl<JobPositionTypeEnum | null>(null);

  private _disabled = false;
  private _value: JobPositionTypeEnum | null = null;
  private _internalSub?: Subscription;

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  private _onChange = (value: JobPositionTypeEnum | null) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched = () => {};

  ngOnInit(): void {
    this._internalSub = this.internalControl.valueChanges.subscribe((value) => {
      this._value = value;
      this._onChange(value);
      this._onTouched();
      this.valueChange.emit(value);
    });
  }

  ngOnDestroy(): void {
    this._internalSub?.unsubscribe();
  }

  writeValue(value: JobPositionTypeEnum | null): void {
    this._value = value;
    this.internalControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: JobPositionTypeEnum | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private _syncDisabled(): void {
    if (this._disabled) {
      this.internalControl.disable({ emitEvent: false });
    } else {
      this.internalControl.enable({ emitEvent: false });
    }
  }

  onSelectionChange(value: JobPositionTypeEnum | null): void {
    this._value = value;
    this._onChange(value);
    this._onTouched();
    this.valueChange.emit(value);
  }

  onBlur(): void {
    this._onTouched();
  }
}
